import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Wallet,
  ArrowUpRight,
  Calendar,
  Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliatePayouts = () => {
  const [stats, setStats] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'chapa',
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    phone_number: ''
  });

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const fetchPayoutData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('enakid_token');
      
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/affiliates/payout/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/affiliates/payout/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setPayoutHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    try {
      // Validation
      if (!formData.amount || parseFloat(formData.amount) < 100) {
        toast.error('Minimum payout amount is $100');
        return;
      }

      if (!formData.account_holder_name || !formData.account_number || !formData.bank_name) {
        toast.error('Please fill in all required fields');
        return;
      }

      const token = Cookies.get('enakid_token');
      const response = await axios.post(
        `${API}/api/affiliates/payout/request`,
        {
          amount: parseFloat(formData.amount),
          payment_method: formData.payment_method,
          account_holder_name: formData.account_holder_name,
          account_number: formData.account_number,
          bank_name: formData.bank_name,
          phone_number: formData.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payout request submitted successfully!');
      setShowRequestModal(false);
      setFormData({
        amount: '',
        payment_method: 'chapa',
        account_holder_name: '',
        account_number: '',
        bank_name: '',
        phone_number: ''
      });
      fetchPayoutData();
    } catch (error) {
      console.error('Error requesting payout:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit payout request';
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Review' },
      approved: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'Approved' },
      processing: { color: 'bg-purple-100 text-purple-800', icon: TrendingUp, label: 'Processing' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payout Management</h1>
          <p className="text-gray-500 mt-1">Request and track your affiliate earnings payouts</p>
        </div>
        <Button 
          onClick={() => setShowRequestModal(true)}
          className="bg-green-600 hover:bg-green-700"
          disabled={!stats || stats.available_balance < 100}
        >
          <ArrowUpRight className="h-4 w-4 mr-2" />
          Request Payout
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${stats?.available_balance?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">Ready for withdrawal</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              ${stats?.pending_payouts?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">Being processed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid Out</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${stats?.total_paid_out?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">Lifetime</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              ${stats?.total_earnings?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Payout Guidelines:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Minimum payout amount: $100</li>
                <li>Maximum 1 payout request per month</li>
                <li>Processing fee: 2% of payout amount</li>
                <li>Payouts are processed manually within 3-5 business days</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payout History */}
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
          <CardDescription>
            Track all your payout requests and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payoutHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No payout requests yet.</p>
              <Button 
                onClick={() => setShowRequestModal(true)} 
                className="mt-4"
                disabled={!stats || stats.available_balance < 100}
              >
                Request Your First Payout
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {payoutHistory.map((payout) => (
                <div
                  key={payout.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-semibold text-lg text-gray-900">
                          ${payout.requested_amount?.toFixed(2)}
                        </p>
                        {getStatusBadge(payout.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Requested:</span> {formatDate(payout.requested_at)}
                        </div>
                        {payout.processed_at && (
                          <div>
                            <span className="font-medium">Processed:</span> {formatDate(payout.processed_at)}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Method:</span> {payout.payment_method?.toUpperCase()}
                        </div>
                        <div>
                          <span className="font-medium">Net Amount:</span> ${payout.net_amount?.toFixed(2)}
                        </div>
                        {payout.account_holder_name && (
                          <div className="col-span-2">
                            <span className="font-medium">Account:</span> {payout.account_holder_name} - {payout.bank_name}
                          </div>
                        )}
                      </div>

                      {payout.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                          <span className="font-medium">Rejection Reason:</span> {payout.rejection_reason}
                        </div>
                      )}

                      {payout.admin_notes && (
                        <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                          <span className="font-medium">Admin Notes:</span> {payout.admin_notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Payout Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              Submit a payout request for your available earnings. Payouts are processed manually within 3-5 business days.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Available Balance Display */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-800">Available Balance:</span>
                <span className="text-2xl font-bold text-green-600">
                  ${stats?.available_balance?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                Payout Amount <span className="text-red-500">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="100"
                max={stats?.available_balance}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="100.00"
              />
              <p className="text-xs text-gray-500">
                Minimum: $100 | Maximum: ${stats?.available_balance?.toFixed(2) || '0.00'}
              </p>
              {formData.amount && parseFloat(formData.amount) >= 100 && (
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Requested Amount:</span>
                    <span className="font-medium">${parseFloat(formData.amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Processing Fee (2%):</span>
                    <span className="font-medium">-${(parseFloat(formData.amount) * 0.02).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-semibold">You'll Receive:</span>
                    <span className="font-bold text-green-600">
                      ${(parseFloat(formData.amount) * 0.98).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label htmlFor="payment_method">Payment Method</Label>
              <Input
                id="payment_method"
                value="Chapa (Bank Transfer)"
                disabled
              />
            </div>

            {/* Account Holder Name */}
            <div className="space-y-2">
              <Label htmlFor="account_holder_name">
                Account Holder Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="account_holder_name"
                value={formData.account_holder_name}
                onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                placeholder="Full name as on bank account"
              />
            </div>

            {/* Bank Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bank_name">
                  Bank Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="bank_name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., Commercial Bank of Ethiopia"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_number">
                  Account Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="account_number"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="Account number"
                />
              </div>
            </div>

            {/* Phone Number (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number (Optional)</Label>
              <Input
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="+251 XXX XXX XXX"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRequestPayout} className="bg-green-600 hover:bg-green-700">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliatePayouts;
